/* EFX storefront runtime — header, mega, drawer, hero finishes, reveal, cart count.
   No framework. Loaded with type="module" (deferred by default). */
const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---------- Header: stuck state ---------- */
const header = $('.efx-header');
if (header) {
  const sentinel = document.createElement('div');
  sentinel.style.cssText = 'position:absolute;top:0;height:1px;width:1px;pointer-events:none;';
  header.parentNode.insertBefore(sentinel, header);
  new IntersectionObserver(([e]) => header.classList.toggle('is-stuck', !e.isIntersecting && window.scrollY > 8), { threshold: 0 }).observe(sentinel);
}

/* ---------- Header: mobile hide-on-down / reveal-on-up (R7 ruling, sages/round-7-header-scroll.md) ----------
   Desktop stays solid and still. Under 1024 px the bar leaves after >100 px of cumulative
   down-travel past scrollY 400 and comes back after >=24 px up — by transform only (the
   layout rule in CLAUDE.md), 200/160 ms. Never hidden while the mega, the drawer or the
   search overlay is open, while the header holds focus, during the configurator build,
   or under prefers-reduced-motion (a 1 ms teleport is worse than a still bar). Publishes
   --efx-header-visible so the collection filter bar / search sub-bar can follow with
   --efx-sticky-top instead of sticking to an empty 64 px band. */
if (header) {
  const root = document.documentElement;
  const mq = matchMedia('(max-width: 1023px)');
  let lastY = window.scrollY, downAcc = 0, upAcc = 0, hidden = false, ticking = false;
  const setHidden = (h) => {
    if (hidden === h) return;
    hidden = h;
    header.classList.toggle('is-hidden', h);
    root.style.setProperty('--efx-header-visible', h ? '0' : '1');
  };
  const searchOpen = () => { const o = document.getElementById('ef-search-overlay'); return !!o && o.getAttribute('aria-hidden') === 'false'; };
  const guarded = () => reduced || !mq.matches || header.classList.contains('has-mega-open') || searchOpen() ||
    header.matches(':focus-within') || document.body.classList.contains('efx-building') || !!document.querySelector('.efx-drawer.is-open');
  const tick = () => {
    ticking = false;
    const y = window.scrollY, dy = y - lastY; lastY = y;
    /* Owner 2026-09-02 21:40: "the thin glow only shows the first time". The sentinel
       IntersectionObserver above fires on wheel input but was measured NOT firing for
       programmatic / momentum scrolls (scrollBy steps: is-stuck stayed false through three
       0→600→0 cycles). The stuck state is a pure function of scrollY, so it is set here on
       every scroll frame as well; the observer stays as a no-cost second source. */
    header.classList.toggle('is-stuck', y > 8);
    if (dy > 0) { downAcc += dy; upAcc = 0; } else if (dy < 0) { upAcc += -dy; downAcc = 0; }
    if (guarded()) { setHidden(false); return; }
    if (!hidden && downAcc > 100 && y > 400) setHidden(true);
    else if (hidden && (upAcc >= 24 || y <= 8)) setHidden(false);
  };
  addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(tick); } }, { passive: true });
  header.addEventListener('focusin', () => setHidden(false));
  (mq.addEventListener ? mq.addEventListener('change', () => setHidden(false)) : mq.addListener(() => setHidden(false)));
  root.style.setProperty('--efx-header-visible', '1');
}

/* ---------- Mega menu: W3C APG disclosure navigation ----------
   The trigger is a <button aria-expanded aria-controls>, NOT a link with
   aria-haspopup: haspopup on a link claims role=menu, and the APG disclosure
   pattern explicitly does not use the menu role. The category link is the
   first item inside the panel instead.
   Hover-intent 250ms in / 400ms out (T-MEGA; NN/g measures 500/500, 250 is the
   compromise for a six-item bar). Escape closes and returns focus; focus
   leaving the header closes, so focus can never land inside a closed panel. */
const megaItems = $$('.efx-nav__item[data-mega]');
let openTimer = 0, closeTimer = 0;
let megaOpener = null;
const MEGA_IN = 250, MEGA_OUT = 400;
function openMega(item, focusFirst = false) {
  clearTimeout(closeTimer);
  megaItems.forEach(i => {
    const on = i === item;
    i.classList.toggle('is-open', on);
    const t = i.querySelector('.efx-nav__link');
    if (t) t.setAttribute('aria-expanded', String(on));
  });
  header.classList.add('has-mega-open');
  megaOpener = item.querySelector('.efx-nav__link');
  if (focusFirst) { const first = item.querySelector('.efxm a, .efxm button'); first && first.focus(); }
}
function closeMega(returnFocus = false) {
  clearTimeout(openTimer);
  const wasOpen = header && header.classList.contains('has-mega-open');
  megaItems.forEach(i => { i.classList.remove('is-open'); const t = i.querySelector('.efx-nav__link'); if (t) t.setAttribute('aria-expanded', 'false'); });
  header && header.classList.remove('has-mega-open');
  if (returnFocus && wasOpen && megaOpener) megaOpener.focus();
}
megaItems.forEach(item => {
  const trigger = item.querySelector('.efx-nav__link');
  if (!trigger) return;
  // The panel is a DOM child of the item, so pointerenter/leave on the item
  // already covers moving the mouse from the bar down into the panel.
  item.addEventListener('pointerenter', e => { if (e.pointerType !== 'mouse') return; clearTimeout(closeTimer); openTimer = setTimeout(() => openMega(item), MEGA_IN); });
  item.addEventListener('pointerleave', e => { if (e.pointerType !== 'mouse') return; clearTimeout(openTimer); closeTimer = setTimeout(closeMega, MEGA_OUT); });
  trigger.addEventListener('click', e => {
    if (item.classList.contains('is-open')) closeMega(true);
    else openMega(item, e.detail === 0);   // keyboard activation moves focus in; a mouse click does not
  });
});
// Real category clips only play while their recognition row is intentionally
// hovered or keyboard-focused. Reduced-motion visitors always keep the poster.
$$('.efxm__type-video').forEach(video => {
  const row = video.closest('.efxm__type');
  if (!row || reduced) return;
  const play = () => video.play().catch(() => {});
  const stop = () => { video.pause(); video.currentTime = 0; };
  row.addEventListener('pointerenter', play);
  row.addEventListener('pointerleave', stop);
  row.addEventListener('focusin', play);
  row.addEventListener('focusout', stop);
});
document.addEventListener('keydown', e => {
  if (e.key !== 'Escape') return;
  closeMega(true);
  // Inside the drawer, Escape pops the level-2 panel first, then closes.
  if (!closeDrawerLevel(true)) closeDrawer();
});
// Close the mega when focus leaves header + panel (keyboard users tabbing past it).
document.addEventListener('focusin', e => { if (header && header.classList.contains('has-mega-open') && !header.contains(e.target)) closeMega(); });
document.addEventListener('click', e => { if (header && !header.contains(e.target)) closeMega(); });

/* ---------- Mobile drawer ---------- */
const drawer = $('.efx-drawer');
const inertTargets = () => $$('#efx-main, #main, .efx-footer, .efx-header, .efx-utility');
function openDrawer() {
  if (!drawer) return;
  drawer.classList.add('is-open'); drawer.setAttribute('aria-hidden', 'false'); drawer.setAttribute('aria-modal', 'true');
  document.documentElement.style.overflow = 'hidden';
  inertTargets().forEach(el => { el.inert = true; });
  $('.efx-burger')?.setAttribute('aria-expanded', 'true');
  $('.efx-drawer__close', drawer)?.focus({ preventScroll: true });
}
function closeDrawer() {
  if (!drawer || !drawer.classList.contains('is-open')) return;
  closeDrawerLevel();
  drawer.classList.remove('is-open'); drawer.setAttribute('aria-hidden', 'true'); drawer.removeAttribute('aria-modal');
  document.documentElement.style.overflow = '';
  inertTargets().forEach(el => { el.inert = false; });
  const burger = $('.efx-burger');
  burger?.setAttribute('aria-expanded', 'false');
  burger?.focus({ preventScroll: true });
}
// Focus trap inside the drawer (belt and braces for browsers without inert).
drawer?.addEventListener('keydown', e => {
  if (e.key !== 'Tab') return;
  const f = $$('a[href], button, input, select, textarea, summary, [tabindex]:not([tabindex="-1"])', drawer)
    .filter(el => el.offsetParent !== null && !el.closest('[inert]'));
  if (!f.length) return;
  const first = f[0], last = f[f.length - 1];
  if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
  else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
});
$('.efx-burger')?.addEventListener('click', openDrawer);
$$('[data-drawer-close]').forEach(el => el.addEventListener('click', closeDrawer));

/* ---------- Mobile drawer: push level-2 panels ----------
   Not <details> accordions: six categories of ~18 links each is 900+px of
   scroll and the buyer loses their place. Level 1 goes inert while a level-2
   panel is open, so Tab can never reach a row that is off-screen. */
const dLevel1 = $('[data-efxd-l1]');
let dPusher = null;
let dActivePanel = null;
let dOpenFrame = 0;
let dHideTimer = 0;
function openDrawerLevel(id, opener) {
  const panel = document.getElementById(id);
  if (!panel || panel === dActivePanel) return;
  cancelAnimationFrame(dOpenFrame);
  clearTimeout(dHideTimer);
  $$('[data-efxd-l2]').forEach(p => {
    if (p !== panel) { p.classList.remove('is-open'); p.hidden = true; p.inert = true; }
  });
  panel.classList.remove('is-open');
  panel.hidden = false;
  panel.inert = false;
  panel.scrollTop = 0;
  dActivePanel = panel;
  dPusher = opener || null;
  if (dLevel1) dLevel1.inert = true;
  $$('[data-efxd-push]').forEach(b => b.setAttribute('aria-expanded', String(b.getAttribute('aria-controls') === id)));
  // Commit the offscreen start before the next frame changes the transform.
  if (reduced) panel.classList.add('is-open');
  else {
    void panel.offsetWidth;
    dOpenFrame = requestAnimationFrame(() => {
      dOpenFrame = 0;
      if (dActivePanel === panel) panel.classList.add('is-open');
    });
  }
  // Focusing a translated child must not scroll its overflow-hidden parent.
  $('.efxd__back', panel)?.focus({ preventScroll: true });
}
function closeDrawerLevel(returnFocus = false) {
  const panel = dActivePanel;
  if (!panel) return false;
  dActivePanel = null;
  cancelAnimationFrame(dOpenFrame);
  dOpenFrame = 0;
  clearTimeout(dHideTimer);
  panel.classList.remove('is-open');
  panel.inert = true;
  const hide = () => { if (dActivePanel !== panel) panel.hidden = true; };
  if (reduced) hide();
  else dHideTimer = setTimeout(hide, 420);
  if (dLevel1) dLevel1.inert = false;
  $$('[data-efxd-push]').forEach(b => b.setAttribute('aria-expanded', 'false'));
  if (returnFocus && dPusher) dPusher.focus({ preventScroll: true });
  dPusher = null;
  return true;
}
$$('[data-efxd-push]').forEach(btn => btn.addEventListener('click', () => openDrawerLevel(btn.getAttribute('aria-controls'), btn)));
$$('[data-efxd-back]').forEach(btn => btn.addEventListener('click', () => closeDrawerLevel(true)));

/* ---------- Search: open the EF overlay if present, else go to /search ---------- */
$$('[data-efx-search]').forEach(btn => btn.addEventListener('click', e => {
  const overlay = $('#ef-search-overlay, .ef-search-overlay, [data-ef-search-overlay]');
  if (typeof window.efSearchOpen === 'function') { e.preventDefault(); window.efSearchOpen(); return; }
  if (window.EFSearch && typeof window.EFSearch.open === 'function') { e.preventDefault(); window.EFSearch.open(); return; }
  if (overlay) { e.preventDefault(); document.dispatchEvent(new CustomEvent('ef:search:open')); overlay.classList.add('is-open'); overlay.setAttribute('aria-hidden', 'false'); overlay.removeAttribute('inert'); const inp = overlay.querySelector('input'); inp && setTimeout(() => inp.focus(), 50); }
}));

/* ---------- AI photo-ID: open the existing EF modal when its bootstrap is present ---------- */
$$('[data-ef-photo-id]').forEach(el => el.addEventListener('click', e => {
  if (typeof window.efOpenAIModal === 'function') { e.preventDefault(); window.efOpenAIModal(); }
}));

/* ---------- Cart: count + drawer trigger ---------- */
async function refreshCartCount() {
  try {
    const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    const cart = await r.json();
    // Surcharge lines are hidden helpers; count only parent products for the badge.
    const visible = cart.items.filter(i => !(i.properties && i.properties._parent_product)).reduce((n, i) => n + i.quantity, 0);
    $$('[data-efx-cart-count]').forEach(el => { el.textContent = visible; el.dataset.count = String(visible); });
  } catch { /* offline — leave as rendered */ }
}
document.addEventListener('cart:updated', refreshCartCount);
document.addEventListener('cart:refresh', refreshCartCount);
document.addEventListener('ef:cart:updated', refreshCartCount);
$$('[data-efx-cart]').forEach(a => a.addEventListener('click', e => {
  if (window.EFCartDrawer && typeof window.EFCartDrawer.open === 'function') { e.preventDefault(); window.EFCartDrawer.open(); }
}));

/* ---------- Hero: respect reduced motion for the autoplay video ---------- */
if (reduced) $$('.efx-hero video').forEach(v => { try { v.pause(); v.removeAttribute('autoplay'); } catch {} });

/* ---------- Hero: finish switcher (progressive — only when >1 finish image) ---------- */
$$('.efx-hero').forEach(hero => {
  const imgs = $$('.efx-hero__media img[data-finish]', hero);
  const chips = $$('.efx-finish', hero);
  const caption = $('[data-hero-caption]', hero);
  if (imgs.length < 2 || !chips.length) return;
  chips.forEach(chip => chip.addEventListener('click', () => {
    const key = chip.dataset.finish;
    const target = imgs.find(i => i.dataset.finish === key);
    if (!target) return;
    imgs.forEach(i => i.classList.toggle('is-hidden', i !== target));
    chips.forEach(c => c.setAttribute('aria-pressed', String(c === chip)));
    if (caption && chip.dataset.caption) caption.textContent = chip.dataset.caption;
    const video = $('.efx-hero__media video', hero);
    if (video) { video.pause(); video.classList.add('is-hidden'); }
  }));
});

/* ---------- Tabs (bestsellers by category) ---------- */
$$('.efx-tabs').forEach(list => {
  const tabs = $$('[role="tab"]', list);
  const select = (tab) => {
    tabs.forEach(t => { const on = t === tab; t.setAttribute('aria-selected', String(on)); t.tabIndex = on ? 0 : -1; const panel = document.getElementById(t.getAttribute('aria-controls')); if (panel) { panel.hidden = !on; if (on) $$('.efx-reveal', panel).forEach(el => el.classList.add('is-in')); } });
  };
  tabs.forEach(tab => {
    tab.addEventListener('click', () => select(tab));
    tab.addEventListener('keydown', e => { const i = tabs.indexOf(tab); if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); const n = tabs[(i + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length]; n.focus(); select(n); } });
  });
});

/* ---------- Before/after slider + pair switcher ----------
   One stage shows one pair at a time; the dots (role=tablist) change pairs and, on
   touch, so does a horizontal swipe that starts away from the comparison divider.
   All panes share the stage's --ba, so the divider stays put across a pair change. */
$$('.efx-ba').forEach(ba => {
  const range = $('.efx-ba__range', ba);
  const set = v => ba.style.setProperty('--ba', Math.max(0, Math.min(100, v)) + '%');
  const at = () => parseFloat(getComputedStyle(ba).getPropertyValue('--ba')) || 50;
  range?.addEventListener('input', () => set(range.value));

  /* --- pair switching (only when the stage carries more than one pane) --- */
  const panes = $$('[data-ba-pane]', ba);
  const root = ba.closest('.efx-baset');
  const caps = root ? $$('[data-ba-cap]', root) : [];
  const dots = root ? $$('[data-ba-dot]', root) : [];
  const many = panes.length > 1;
  let idx = 0;
  const show = n => {
    if (!many) return;
    idx = (n + panes.length) % panes.length;
    panes.forEach((p, k) => {
      const on = k === idx;
      p.classList.toggle('is-active', on);
      if (on) { p.removeAttribute('aria-hidden'); $$('img', p).forEach(im => { im.loading = 'eager'; }); }
      else p.setAttribute('aria-hidden', 'true');
    });
    caps.forEach((c, k) => c.classList.toggle('is-active', k === idx));
    dots.forEach((d, k) => { d.classList.toggle('is-active', k === idx); d.setAttribute('aria-selected', String(k === idx)); d.tabIndex = k === idx ? 0 : -1; });
  };
  dots.forEach((d, k) => {
    d.addEventListener('click', () => show(k));
    d.addEventListener('keydown', e => {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      e.preventDefault();
      const n = (k + (e.key === 'ArrowRight' ? 1 : dots.length - 1)) % dots.length;
      show(n); dots[n].focus();
    });
  });

  /* --- pointer: drag the divider, or (touch, away from it) swipe the pair --- */
  let mode = null, sx = 0, sy = 0;
  const fromEvent = e => { const r = ba.getBoundingClientRect(); const v = (e.clientX - r.left) / r.width * 100; set(v); if (range) range.value = v; };
  ba.addEventListener('pointerdown', e => {
    sx = e.clientX; sy = e.clientY;
    const r = ba.getBoundingClientRect();
    const divider = r.left + r.width * at() / 100;
    if (many && e.pointerType === 'touch' && Math.abs(e.clientX - divider) > 48) { mode = 'undecided'; return; }
    mode = 'drag'; fromEvent(e);
  });
  window.addEventListener('pointermove', e => {
    if (mode === 'drag') { fromEvent(e); return; }
    if (mode !== 'undecided') return;
    const dx = e.clientX - sx, dy = e.clientY - sy;
    if (Math.abs(dy) > Math.abs(dx)) { mode = null; return; }   /* vertical: let the page scroll */
    if (Math.abs(dx) < 45) return;
    mode = 'switched';
    show(idx + (dx < 0 ? 1 : -1));
  });
  window.addEventListener('pointerup', () => { mode = null; });
  window.addEventListener('pointercancel', () => { mode = null; });
});

/* ---------- Loox review wall: no empty chrome (T-PROOF) ----------
   The Loox app block renders itself. If the app is slow we wait; if it never renders
   anything (app removed, blocked by a content blocker, no reviews) we hide the wrapper
   AND the heading above it, so the page shows the trust strip + before/after and stops
   cleanly instead of leaving a titled void. We never touch the widget's internals. */
$$('[data-efx-loox]').forEach(wrap => {
  const head = document.querySelector('[data-efx-loox-head]');
  const MOUNTED_PX = 80, DEADLINE_MS = 12000;
  let settled = false;
  const mounted = () => wrap.offsetHeight > MOUNTED_PX;
  const finish = ok => {
    if (settled) return; settled = true;
    obs.disconnect(); clearTimeout(timer);
    if (ok) { wrap.classList.add('is-mounted'); return; }
    wrap.hidden = true; if (head) head.hidden = true;
  };
  const obs = new MutationObserver(() => { if (mounted()) finish(true); });
  obs.observe(wrap, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class', 'height'] });
  const timer = setTimeout(() => finish(mounted()), DEADLINE_MS);
  if (mounted()) finish(true);
});

/* ---------- Card rails: arrows for the peeking row (desktop mouse has no scrollbar) ---------- */
$$('.efx-tabpanel .efx-cards, .efx-rail > .efx-cards').forEach(rail => {
  if (rail.dataset.railBound) return;
  const scrollable = () => rail.scrollWidth - rail.clientWidth > 8;
  const wrap = document.createElement('div');
  wrap.className = 'efx-rail';
  rail.parentNode.insertBefore(wrap, rail);
  wrap.appendChild(rail);

  const mk = (dir) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'efx-rail__nav efx-rail__nav--' + (dir < 0 ? 'prev' : 'next');
    b.setAttribute('aria-label', dir < 0 ? 'Previous products' : 'More products');
    b.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="' + (dir < 0 ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6') + '"/></svg>';
    b.addEventListener('click', () => {
      // At the end of travel the button stays live and simply does nothing. It used to set
      // [disabled] + pointer-events:none, so the arrow vanished from under the cursor and the
      // click landed on the product card behind it.
      if (b.classList.contains('is-end')) return;
      const card = rail.querySelector('.efx-card');
      const step = card ? card.getBoundingClientRect().width + 20 : rail.clientWidth * 0.8;
      rail.scrollBy({ left: dir * step, behavior: reduced ? 'auto' : 'smooth' });
    });
    b.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      b.click();
    });
    wrap.appendChild(b);
    return b;
  };
  const prev = mk(-1), next = mk(1);
  const sync = () => {
    const can = scrollable();
    wrap.classList.toggle('is-scrollable', can);
    prev.classList.toggle('is-end', !can || rail.scrollLeft < 8);
    next.classList.toggle('is-end', !can || rail.scrollLeft > rail.scrollWidth - rail.clientWidth - 8);
  };

  // Drag to pan. A mouse has no way to move this row: there is no visible scrollbar and the
  // wheel scrolls the page, so the arrows were the only route.
  let down = false, startX = 0, startScroll = 0, moved = 0, pid = null, captured = false;
  rail.addEventListener('pointerdown', (e) => {
    if (e.pointerType === 'touch' || e.button !== 0 || !scrollable()) return;
    down = true; moved = 0; startX = e.clientX; startScroll = rail.scrollLeft; pid = e.pointerId;
    // Deliberately NOT capturing here. Capturing on pointerdown retargets the subsequent
    // click to the rail, so every card link went dead (measured: click did not navigate).
    // Capture only once real movement starts — a clean click never gets captured.
  });
  rail.addEventListener('pointermove', (e) => {
    if (!down) return;
    const dx = e.clientX - startX;
    moved = Math.max(moved, Math.abs(dx));
    if (!captured && moved > 5) {
      captured = true;
      rail.classList.add('is-dragging');
      try { rail.setPointerCapture(pid); } catch (_) {}
    }
    if (captured) rail.scrollLeft = startScroll - dx;
  });
  const release = (e) => {
    if (!down) return;
    down = false;
    rail.classList.remove('is-dragging');
    if (captured) { captured = false; try { rail.releasePointerCapture(e.pointerId); } catch (_) {} }
  };
  rail.addEventListener('pointerup', release);
  rail.addEventListener('pointercancel', release);
  // A drag must never open a product. Capture phase, before the card's own link handler.
  rail.addEventListener('click', (e) => {
    if (moved > 5) { e.preventDefault(); e.stopPropagation(); moved = 0; }
  }, true);
  rail.addEventListener('dragstart', (e) => e.preventDefault());

  rail.addEventListener('scroll', sync, { passive: true });
  new ResizeObserver(sync).observe(rail);
  sync();
  rail.dataset.railBound = '1';
});

/* ---------- Reveal on scroll (one autonomous mover per viewport rule: only opacity/translate) ---------- */
if (!reduced && 'IntersectionObserver' in window) {
  const io = new IntersectionObserver(entries => entries.forEach(en => { if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); } }), { rootMargin: '0px 0px -8% 0px' });
  $$('.efx-reveal').forEach(el => io.observe(el));
} else { $$('.efx-reveal').forEach(el => el.classList.add('is-in')); }

refreshCartCount();
